import { useState, useRef, MouseEvent, TouchEvent } from 'react';
import { Tooltip, useTheme } from '@mui/material';
import { SxProps } from '@mui/system';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

type OptionType = string | React.ReactElement;

interface SelectDropdownProps {
  controlledSelectedIndex?: number;
  disabled?: boolean;
  initialIndex?: number;
  onOptionSelect: (index: number) => void;
  onDropdownStateChange?: (open: boolean) => void;
  options: OptionType[];
  placeholder?: OptionType;
  tooltip?: string;
  width?: string;
  className?: string;
  sx?: SxProps;
}

export default function SelectDropdown({
  controlledSelectedIndex,
  disabled,
  initialIndex = 0,
  onOptionSelect,
  onDropdownStateChange,
  options,
  placeholder,
  tooltip,
  width,
  className,
  sx,
}: SelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // Hold the selectedIndex if not controlled from parent
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  // Use controlledSelectedIndex if provided, else use internal selectedIndex
  const effectiveSelectedIndex =
    controlledSelectedIndex !== undefined ? controlledSelectedIndex : selectedIndex;

  // Handle option selection
  const handleMenuItemClick = (index: number) => {
    // Update internal state only if we're in uncontrolled mode
    if (controlledSelectedIndex === undefined) {
      setSelectedIndex(index);
    }
    onOptionSelect(index);
    setOpen(false);
  };

  // Handle dropdown state changes
  const handleToggle = () => {
    const newOpenState = !open;
    setOpen(newOpenState);
    if (onDropdownStateChange) {
      onDropdownStateChange(newOpenState);
    }
  };

  // Close dropdown when clicking away
  const handleClose = (event: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
    if (onDropdownStateChange) {
      // Always closed
      onDropdownStateChange(false);
    }
  };

  // Allow strings or icons as options by creating a unique key for each option
  const generateOptionKey = (option: OptionType, index: number): string => {
    return typeof option === 'string' ? `text-${option}` : `icon-${index}`;
  };

  return (
    <div>
      <ButtonGroup
        variant="text"
        ref={anchorRef}
        aria-label="select dropdown"
        disabled={disabled}
        className={'select-dropdown ' + className}
        sx={sx}
      >
        <Tooltip
          title={tooltip || ''}
          disableInteractive
        >
          <span>
            <Button
              onClick={handleToggle}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="menu"
              sx={{
                width: width || 'auto',
                textTransform: 'unset',
              }}
            >
              {placeholder || options[effectiveSelectedIndex]}
              <ArrowDropDownIcon />
            </Button>
          </span>
        </Tooltip>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1300,
          minHeight: 'fit-content',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose as any}>
                <MenuList
                  id="split-button-menu"
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {options.map((option, index) => (
                    <MenuItem
                      key={generateOptionKey(option, index)}
                      selected={index === selectedIndex}
                      onClick={() => handleMenuItemClick(index)}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}
